//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { ReactNode } from 'react';
import { CircularProgress, Box, Grid, lighten } from '@mui/material';
import styled, { CSSObject } from '@emotion/styled';
import { convertStylesToStyled } from '@/_utils/utils';

type CircularPercentageProps = {
  value: number;
  children: ReactNode;
  size?: number;
  color?: string;
  bgColor?: string;
  style?: CSSObject;
};

function CircularPercentage({
  value,
  children,
  size = 70,
  color = (window as any).theme.palette.success.main,
  bgColor = lighten((window as any).theme.palette.warning.main, 0.2),
  style
}: CircularPercentageProps) {
  return (
    <PercentageWrapperProps item custom={style} size={size}>
      <PercentageBg variant="determinate" value={100} size={size} fill={bgColor} transform={360 * (value / 100)} />
      <Percentage variant="determinate" value={value} size={size} fill={color} />
      <Box top={0} left={0} bottom={0} right={0} position="absolute" display="flex" alignItems="center" justifyContent="center">
        {children}
      </Box>
    </PercentageWrapperProps>
  );
}

type PercentageWrapperProps = {
  custom?: CSSObject;
  size: number;
};
const PercentageWrapperProps = styled(Grid)<PercentageWrapperProps>`
  height: ${({ size }) => size}px;
  width: ${({ size }) => size}px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: ${({ theme }) => theme.spacing(0.75)};
  ${({ custom }) => convertStylesToStyled(custom)}
`;

type ProgressProps = {
  fill: string;
  transform?: number;
};
const PercentageBg = styled(CircularProgress)<ProgressProps>`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: 0 auto;
  color: ${({ fill }) => fill};

  & > svg {
    transform: rotate(${({ transform }) => transform}deg);
  }
`;

const Percentage = styled(CircularProgress)<ProgressProps>`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  margin: 0 auto;
  color: ${({ fill }) => fill};
  z-index: 2;

  & > svg,
  circle {
    stroke-linecap: round;
  }
`;

export default CircularPercentage;
